import { AnswerSource, type SurveyStats } from 'src/redux/survey/survey.types';

export const SOURCE_PARAM_VALUES = ['all', 'human', 'ai', 'rtr'] as const;
export type SourceParamValue = typeof SOURCE_PARAM_VALUES[number];

const SOURCE_PARAM_TO_ANSWER_SOURCE: Record<SourceParamValue, AnswerSource> = {
  all: AnswerSource.All,
  human: AnswerSource.Human,
  ai: AnswerSource.AI,
  rtr: AnswerSource.RTR,
};

const ANSWER_SOURCE_TO_SOURCE_PARAM: Record<AnswerSource, SourceParamValue | null> = {
  [AnswerSource.All]: null,
  [AnswerSource.Human]: 'human',
  [AnswerSource.AI]: 'ai',
  [AnswerSource.RTR]: 'rtr',
};

export interface SourceFilterOption {
  value: AnswerSource;
  label: string;
  paramValue: SourceParamValue;
}

const ALL_SOURCE_OPTIONS: SourceFilterOption[] = [
  { value: AnswerSource.All, label: 'All', paramValue: 'all' },
  { value: AnswerSource.Human, label: 'Human', paramValue: 'human' },
  { value: AnswerSource.AI, label: 'AI', paramValue: 'ai' },
  { value: AnswerSource.RTR, label: 'RTR', paramValue: 'rtr' },
];

export function parseSourceFilter(value: string | null): AnswerSource {
  if (value === 'human') {
    return AnswerSource.Human;
  }
  if (value === 'ai') {
    return AnswerSource.AI;
  }
  if (value === 'rtr') {
    return AnswerSource.RTR;
  }
  return AnswerSource.All;
}

export function serializeSourceFilter(source: AnswerSource): string | null {
  return ANSWER_SOURCE_TO_SOURCE_PARAM[source];
}

export function getAvailableSourceOptions(stats?: SurveyStats): SourceFilterOption[] {
  if (!stats) {
    return ALL_SOURCE_OPTIONS;
  }

  const hasHuman = stats.humanUsers > 0;
  const hasAi = stats.aiUsers > 0;
  const hasRtr = stats.rtrUsers > 0;
  const hasAny = hasHuman || hasAi || hasRtr;

  return ALL_SOURCE_OPTIONS.filter((option) => {
    switch (option.value) {
      case AnswerSource.All:
        return hasAny;
      case AnswerSource.Human:
        return hasHuman;
      case AnswerSource.AI:
        return hasAi;
      case AnswerSource.RTR:
        return hasRtr;
      default:
        return false;
    }
  });
}

export function normaliseSourceFilter(
  sourceFilter: AnswerSource,
  availableOptions: SourceFilterOption[]
): AnswerSource {
  if (availableOptions.some((option) => option.value === sourceFilter)) {
    return sourceFilter;
  }
  return availableOptions[0]?.value ?? AnswerSource.All;
}

export function sourceFilterLabel(sourceFilter: AnswerSource): string {
  switch (sourceFilter) {
    case AnswerSource.Human:
      return 'Human';
    case AnswerSource.AI:
      return 'AI';
    case AnswerSource.RTR:
      return 'RTR';
    default:
      return 'All';
  }
}

export function emptyFeedbackMessage(sourceFilter: AnswerSource): string {
  switch (sourceFilter) {
    case AnswerSource.Human:
      return 'No human feedback on this question';
    case AnswerSource.AI:
      return 'No AI feedback on this question';
    case AnswerSource.RTR:
      return 'No RTR feedback on this question';
    default:
      return 'No feedback on this question';
  }
}

export function emptyFreeformMessage(sourceFilter: AnswerSource): string {
  switch (sourceFilter) {
    case AnswerSource.Human:
      return 'No human freeform responses on this question';
    case AnswerSource.AI:
      return 'No AI freeform responses on this question';
    case AnswerSource.RTR:
      return 'No RTR freeform responses on this question';
    default:
      return 'No freeform responses yet';
  }
}

export function sourceParamToAnswerSource(paramValue: SourceParamValue): AnswerSource {
  return SOURCE_PARAM_TO_ANSWER_SOURCE[paramValue];
}
